import { Injectable, signal } from '@angular/core';

export type Locale = 'de' | 'en';

interface Translations {
  [key: string]: {
    en: string;
    de: string;
  };
}

const TRANSLATIONS: Translations = {
  // Header
  'header.poweredBy': { en: 'Powered by', de: 'Powered by' },
  'header.loading': { en: 'Loading...', de: 'Laden...' },

  // Loading
  'loading.calendar': { en: 'Loading calendar...', de: 'Kalender wird geladen...' },

  // Error
  'error.notFound': { en: 'Page not found', de: 'Seite nicht gefunden' },
  'error.calendarNotFound': { en: 'Calendar not found', de: 'Kalender nicht gefunden' },
  'error.noCalendar': { en: 'No calendar specified', de: 'Kein Kalender angegeben' },
  'error.loadFailed': { en: 'Calendar could not be loaded', de: 'Kalender konnte nicht geladen werden' },
  'error.generic': { en: 'An error occurred', de: 'Ein Fehler ist aufgetreten' },
  'error.invalidRequest': { en: 'Invalid request', de: 'Ungültige Anfrage' },
  'error.slotUnavailable': { en: 'This slot is no longer available', de: 'Dieser Termin ist nicht mehr verfügbar' },
  'error.noConnection': { en: 'No connection to server', de: 'Keine Verbindung zum Server' },
  'error.submitFailed': { en: 'Request could not be sent', de: 'Anfrage konnte nicht gesendet werden' },
  'error.learnMore': { en: 'Learn more about Availme', de: 'Mehr über Availme erfahren' },

  // Not Found page (#455)
  'notFound.customerDescription': {
    en: 'This page does not exist or the address has changed. Visit availme.net to learn more.',
    de: 'Diese Seite existiert nicht oder die Adresse hat sich geändert. Besuche availme.net, um mehr zu erfahren.',
  },
  'notFound.cta.availme': { en: 'Go to Availme', de: 'Zu Availme' },

  // Booking page
  'booking.selectSlot': { en: 'Select your preferred appointment', de: 'Wählen Sie Ihren Wunschtermin aus' },
  'booking.requestTitle': { en: 'Request appointment - ', de: 'Termin anfragen - ' },

  // Week view
  'weekView.today': { en: 'Today', de: 'Heute' },
  'weekView.availableSlots': { en: 'Available appointments', de: 'Verfügbare Termine' },
  'weekView.slotsCount': { en: '{count} Slots', de: '{count} Termine' },
  'weekView.noSlots': { en: 'No available appointments on this day', de: 'Keine freien Termine an diesem Tag' },
  'weekView.showNextAvailable': { en: 'Show next available day', de: 'Nächsten verfügbaren Tag anzeigen' },
  'weekView.until': { en: 'to', de: 'bis' },
  'weekView.minutes': { en: 'min', de: 'Min' },
  'weekView.timesIn': { en: 'Times in', de: 'Zeiten in' },
  'weekView.timezoneExplanation': { en: 'Appointments are displayed in the provider\'s timezone.', de: 'Termine werden in der Zeitzone des Anbieters angezeigt.' },
  'weekView.yourTime': { en: 'Your time', de: 'Ihre Zeit' },
  'weekView.hoursAhead': { en: 'hours ahead', de: 'Stunden voraus' },
  'weekView.hoursBehind': { en: 'hours behind', de: 'Stunden zurück' },
  'weekView.sameTimezone': { en: 'Same timezone as you', de: 'Gleiche Zeitzone wie Sie' },

  // Booking form
  'form.selectedSlot': { en: 'Selected appointment', de: 'Ausgewählter Termin' },
  'form.changeSlot': { en: 'Change appointment', de: 'Termin ändern' },
  'form.contactData': { en: 'Your contact details', de: 'Ihre Kontaktdaten' },
  'form.at': { en: 'at', de: 'um' },
  'form.clock': { en: '', de: 'Uhr' },
  'form.name': { en: 'Name', de: 'Name' },
  'form.namePlaceholder': { en: 'Your name', de: 'Ihr Name' },
  'form.nameRequired': { en: 'Name is required', de: 'Name ist erforderlich' },
  'form.firstName': { en: 'First name', de: 'Vorname' },
  'form.lastName': { en: 'Last name', de: 'Nachname' },
  'form.email': { en: 'Email', de: 'E-Mail' },
  'form.emailPlaceholder': { en: 'your@email.com', de: 'ihre@email.de' },
  'form.emailRequired': { en: 'Email is required', de: 'E-Mail ist erforderlich' },
  'form.emailInvalid': { en: 'Please enter a valid email address', de: 'Bitte geben Sie eine gültige E-Mail-Adresse ein' },
  'form.phone': { en: 'Phone (optional)', de: 'Telefon (optional)' },
  'form.message': { en: 'Message (optional)', de: 'Nachricht (optional)' },
  'form.messagePlaceholder': { en: 'Describe your concern...', de: 'Beschreiben Sie Ihr Anliegen...' },
  'form.consent': { en: 'I agree to the processing of my data for the purpose of appointment booking.', de: 'Ich stimme der Verarbeitung meiner Daten zum Zweck der Terminbuchung zu.' },
  'form.submit': { en: 'Submit appointment request', de: 'Terminanfrage absenden' },
  'form.submitting': { en: 'Submitting...', de: 'Wird gesendet...' },

  // Success
  'success.title': { en: 'Appointment request submitted!', de: 'Terminanfrage erfolgreich übermittelt!' },
  'success.message': { en: 'Your appointment request has been successfully submitted.', de: 'Ihre Terminanfrage wurde erfolgreich übermittelt.' },
  'success.requestedSlot': { en: 'Requested appointment', de: 'Angefragter Termin' },
  'success.clock': { en: '', de: 'Uhr' },
  'success.at': { en: 'at', de: 'um' },
  'success.nextSteps': { en: 'Next steps', de: 'Nächste Schritte' },
  'success.step1': { en: 'You will receive a confirmation email shortly', de: 'Sie erhalten in Kürze eine Bestätigungs-E-Mail' },
  'success.step2': { en: 'Confirm the offer to book the appointment', de: 'Bestätigen Sie das Angebot um den Termin zu buchen' },
  'success.step3': { en: 'After confirmation, the appointment is booked', de: 'Nach der Bestätigung ist der Termin gebucht' },
  'success.requestId': { en: 'Request ID', de: 'Anfrage-ID' },
  'success.newRequest': { en: 'New appointment request', de: 'Neue Terminanfrage' },

  // Footer
  'footer.copyright': { en: 'All rights reserved.', de: 'Alle Rechte vorbehalten.' },
  'footer.createdWith': { en: 'Created with', de: 'Erstellt mit' },

  // Cookie banner
  'cookie.message': { en: 'We use technically necessary cookies to make your visit more comfortable.', de: 'Wir verwenden technisch notwendige Cookies, um Ihren Besuch angenehmer zu gestalten.' },
  'cookie.learnMore': { en: 'Learn more', de: 'Mehr erfahren' },
  'cookie.dismiss': { en: 'Got it', de: 'Verstanden' },

  // Preview mode
  'preview.badge': { en: 'Preview', de: 'Vorschau' },
  'preview.formDisabled': { en: 'The booking form is disabled in preview mode.', de: 'Das Buchungsformular ist im Vorschaumodus deaktiviert.' },
  'preview.heroPlaceholder': { en: 'Hero Section', de: 'Hero-Sektion' },
  'preview.heroPlaceholderHint': { en: 'Add a title and subtitle in the editor', de: 'Fügen Sie im Editor einen Titel und Untertitel hinzu' },

  // Offer acceptance page
  'offer.title': { en: 'Your appointment offer', de: 'Ihr Terminangebot' },
  'offer.from': { en: 'from', de: 'von' },
  'offer.appointmentDetails': { en: 'Appointment details', de: 'Termindetails' },
  'offer.date': { en: 'Date', de: 'Datum' },
  'offer.time': { en: 'Time', de: 'Uhrzeit' },
  'offer.duration': { en: 'Duration', de: 'Dauer' },
  'offer.durationMinutes': { en: 'minutes', de: 'Minuten' },
  'offer.estimatedPrice': { en: 'Estimated price', de: 'Geschätzter Preis' },
  'offer.provider': { en: 'Provider', de: 'Anbieter' },
  'offer.trade': { en: 'Service', de: 'Leistung' },
  'offer.message': { en: 'Message from provider', de: 'Nachricht vom Anbieter' },
  'offer.expiresAt': { en: 'This offer expires on', de: 'Dieses Angebot läuft ab am' },
  'offer.expiresIn': { en: 'Expires in', de: 'Läuft ab in' },
  'offer.hours': { en: 'hours', de: 'Stunden' },

  // Offer acceptance form
  'offer.yourDetails': { en: 'Your details', de: 'Ihre Angaben' },
  'offer.customerName': { en: 'Name', de: 'Name' },
  'offer.customerNamePlaceholder': { en: 'Your full name', de: 'Ihr vollständiger Name' },
  'offer.customerNameRequired': { en: 'Name is required', de: 'Name ist erforderlich' },
  'offer.street': { en: 'Street', de: 'Straße' },
  'offer.streetPlaceholder': { en: 'Street name', de: 'Straßenname' },
  'offer.streetRequired': { en: 'Street is required', de: 'Straße ist erforderlich' },
  'offer.houseNumber': { en: 'House number', de: 'Hausnummer' },
  'offer.houseNumberPlaceholder': { en: 'No.', de: 'Nr.' },
  'offer.houseNumberRequired': { en: 'House number is required', de: 'Hausnummer ist erforderlich' },
  'offer.city': { en: 'City', de: 'Stadt' },
  'offer.cityPlaceholder': { en: 'City', de: 'Stadt' },
  'offer.cityRequired': { en: 'City is required', de: 'Stadt ist erforderlich' },
  'offer.postalCode': { en: 'Postal code', de: 'Postleitzahl' },
  'offer.postalCodePlaceholder': { en: '12345', de: '12345' },
  'offer.postalCodeRequired': { en: 'Postal code is required', de: 'Postleitzahl ist erforderlich' },
  'offer.phone': { en: 'Phone (optional)', de: 'Telefon (optional)' },
  'offer.phonePlaceholder': { en: '+49 123 456789', de: '+49 123 456789' },

  // Offer consent
  'offer.termsConsent': { en: 'I agree to the general terms and conditions', de: 'Ich stimme den allgemeinen Geschäftsbedingungen zu' },
  'offer.viewTerms': { en: 'View terms', de: 'AGB ansehen' },
  'offer.termsRequired': { en: 'You must accept the terms to continue', de: 'Sie müssen den Bedingungen zustimmen, um fortzufahren' },
  'offer.privacyConsent': { en: "I agree to the provider's privacy policy", de: 'Ich stimme der Datenschutzerklärung des Anbieters zu' },
  'offer.privacyRequired': { en: 'You must accept the privacy policy to continue', de: 'Sie müssen der Datenschutzerklärung zustimmen, um fortzufahren' },
  'offer.viewPrivacyPolicy': { en: 'View privacy policy', de: 'Datenschutzerklärung ansehen' },

  // Offer actions
  'offer.accept': { en: 'Accept offer', de: 'Angebot annehmen' },
  'offer.accepting': { en: 'Accepting...', de: 'Wird angenommen...' },
  'offer.decline': { en: 'Decline offer', de: 'Angebot ablehnen' },
  'offer.declining': { en: 'Declining...', de: 'Wird abgelehnt...' },
  'offer.declineReason': { en: 'Reason for declining (optional)', de: 'Grund für die Ablehnung (optional)' },
  'offer.declineReasonPlaceholder': { en: 'Let us know why you are declining...', de: 'Teilen Sie uns mit, warum Sie ablehnen...' },

  // Offer status messages
  'offer.statusAccepted': { en: 'Offer accepted!', de: 'Angebot angenommen!' },
  'offer.statusAcceptedMessage': { en: 'Your appointment has been confirmed. You will receive a confirmation email shortly.', de: 'Ihr Termin wurde bestätigt. Sie erhalten in Kürze eine Bestätigungs-E-Mail.' },
  'offer.statusDeclined': { en: 'Offer declined', de: 'Angebot abgelehnt' },
  'offer.statusDeclinedMessage': { en: 'You have declined this offer.', de: 'Sie haben dieses Angebot abgelehnt.' },
  'offer.statusExpired': { en: 'Offer expired', de: 'Angebot abgelaufen' },
  'offer.statusExpiredMessage': { en: 'This offer has expired and is no longer available.', de: 'Dieses Angebot ist abgelaufen und nicht mehr verfügbar.' },
  'offer.statusAlreadyAccepted': { en: 'Already accepted', de: 'Bereits angenommen' },
  'offer.statusAlreadyAcceptedMessage': { en: 'This offer has already been accepted.', de: 'Dieses Angebot wurde bereits angenommen.' },
  'offer.requestClosed': { en: 'Request closed', de: 'Anfrage geschlossen' },
  'offer.requestClosedMessage': { en: 'This request has been closed and cannot accept new offers.', de: 'Diese Anfrage wurde geschlossen und kann keine neuen Angebote annehmen.' },

  // Offer timeline
  'offer.timelineRequested': { en: 'Requested', de: 'Angefragt' },
  'offer.timelineOfferSent': { en: 'Offer sent', de: 'Angebot gesendet' },
  'offer.timelinePending': { en: 'Your response', de: 'Ihre Antwort' },

  // Offer errors
  'offer.errorLoading': { en: 'Could not load offer details', de: 'Angebotsdetails konnten nicht geladen werden' },
  'offer.errorNotFound': { en: 'Offer not found', de: 'Angebot nicht gefunden' },
  'offer.errorAccepting': { en: 'Could not accept offer', de: 'Angebot konnte nicht angenommen werden' },
  'offer.errorDeclining': { en: 'Could not decline offer', de: 'Angebot konnte nicht abgelehnt werden' },
  'offer.errorGeneric': { en: 'An error occurred', de: 'Ein Fehler ist aufgetreten' },
};

const LOCALE_STORAGE_KEY = 'dc_locale';

@Injectable({
  providedIn: 'root',
})
export class PublicI18nService {
  private readonly localeSignal = signal<Locale>(this.detectLocale());

  readonly locale = this.localeSignal.asReadonly();

  /**
   * Detect locale from URL parameter, localStorage, or browser settings.
   * Priority: URL ?lang= param > localStorage > browser language > default 'de'
   */
  private detectLocale(): Locale {
    if (typeof window !== 'undefined') {
      // Check URL parameter first
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get('lang');
      if (langParam === 'en' || langParam === 'de') {
        return langParam;
      }

      // Check localStorage
      const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
      if (storedLocale === 'en' || storedLocale === 'de') {
        return storedLocale;
      }

      // Fall back to browser language
      const browserLang = navigator.language?.split('-')[0];
      if (browserLang === 'en') {
        return 'en';
      }
    }
    return 'de';
  }

  /**
   * Set the current locale and persist to localStorage
   */
  setLocale(locale: Locale): void {
    this.localeSignal.set(locale);
    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
  }

  /**
   * Translate a key to the current locale
   */
  t(key: string): string {
    const translation = TRANSLATIONS[key];
    if (!translation) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    return translation[this.locale()];
  }

  /**
   * Get the locale string for date formatting (e.g., 'de-DE', 'en-US')
   */
  getDateLocale(): string {
    return this.locale() === 'de' ? 'de-DE' : 'en-US';
  }

  /**
   * Get day abbreviations for the current locale
   */
  getDayNames(): string[] {
    if (this.locale() === 'de') {
      return ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    }
    return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  }

  /**
   * Format time string (HH:mm) according to locale.
   * German: 24h format (09:00)
   * English: 12h format with AM/PM (9:00 AM)
   */
  formatTime(time: string): string {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);

    if (this.locale() === 'de') {
      return time; // Keep 24h format: 09:00
    }

    // English: Convert to 12h format
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  /**
   * Format a timezone identifier to a short display name.
   * Example: "Europe/Berlin" -> "Berlin (CET)" or "Berlin (CEST)" depending on DST
   */
  formatTimezoneShort(timezone: string): string {
    if (!timezone) return '';

    try {
      // Get the city name from the timezone identifier
      const cityName = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;

      // Get the abbreviated timezone name (e.g., CET, CEST, EST, PST)
      const now = new Date();
      const formatter = new Intl.DateTimeFormat(this.getDateLocale(), {
        timeZone: timezone,
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(now);
      const tzAbbr = parts.find(p => p.type === 'timeZoneName')?.value || '';

      return `${cityName} (${tzAbbr})`;
    } catch {
      // Fallback: just return the timezone as-is
      return timezone;
    }
  }

  /**
   * Calculate the hour offset between two timezones.
   * Returns positive number if viewerTz is ahead, negative if behind.
   */
  getTimezoneOffsetHours(providerTz: string, viewerTz: string): number {
    if (!providerTz || !viewerTz || providerTz === viewerTz) {
      return 0;
    }

    try {
      const now = new Date();

      // Get offset in minutes for provider timezone
      const providerFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: providerTz,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      });
      const providerParts = providerFormatter.formatToParts(now);
      const providerHour = parseInt(providerParts.find(p => p.type === 'hour')?.value || '0');
      const providerMinute = parseInt(providerParts.find(p => p.type === 'minute')?.value || '0');

      // Get offset in minutes for viewer timezone
      const viewerFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: viewerTz,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      });
      const viewerParts = viewerFormatter.formatToParts(now);
      const viewerHour = parseInt(viewerParts.find(p => p.type === 'hour')?.value || '0');
      const viewerMinute = parseInt(viewerParts.find(p => p.type === 'minute')?.value || '0');

      // Calculate difference in hours (accounting for day boundary)
      let hourDiff = viewerHour - providerHour;
      const minuteDiff = viewerMinute - providerMinute;

      // Adjust for day boundary (e.g., 23:00 vs 01:00)
      if (hourDiff > 12) hourDiff -= 24;
      if (hourDiff < -12) hourDiff += 24;

      // Round to nearest half hour if there's a minute difference
      return hourDiff + (minuteDiff / 60);
    } catch {
      return 0;
    }
  }
}
